<template>
  <AdminLayout>
    <v-row align="stretch" class="mb-16 gutters">
      <v-col cols="12" class="mx-xs-auto d-flex flex-column height">
        <v-form ref="form" v-model="valid">
          <v-row>
            <v-col>
              <v-text-field
                variant="outlined"
                density="compact"
                hide-details="auto"
                placeholder="Benutzer"
                clearable
                v-model="newUserId"
                :rules="rules.email"
              >
              </v-text-field>
            </v-col>
            <v-col>
              <v-select
                variant="outlined"
                density="compact"
                multiple
                v-model="newRoleIds"
                :items="api.roles"
                item-title="name"
                item-value="id"
                clearable
                chips
              ></v-select>
            </v-col>
            <v-col cols="auto">
              <v-btn color="primary" @click="addTenantUser">Hinzufügen</v-btn>
            </v-col>
          </v-row>
        </v-form>

        <v-data-table
          :headers="headers"
          :items="members"
          :footer-props="{
            'items-per-page-all-text': 'Alle',
            'items-per-page-text': 'Benutzer pro Seite',
          }"
          class="bg-accent elevation-1 mt-2"
          fixed-header
          :loading="loading"
          loading-text="Daten werden geladen..."
        >
          <template #loader>
            <v-progress-linear
              indeterminate
              color="primary"
              height="4"
            ></v-progress-linear>
          </template>
          <template v-slot:item.roles="{ item }">
            <template v-for="(roleId, i) in item.roles">
              <template v-if="getRoleById(roleId)">
                <v-chip
                  size="small"
                  closable
                  variant="flat"
                  class="mr-1 text-black"
                  color="secondary"
                  :key="roleId"
                  @click:close="removeTenantUserRole(item.userId, roleId)"
                  >{{ getRoleById(roleId)?.name }}</v-chip
                >
              </template>
            </template>
          </template>
          <template v-slot:item.groups="{ item }">
            <div class="pa-2 chip-wrapper">
              <template v-for="(groupId, i) in item.groups">
                <template v-if="getUserGroupById(groupId)">
                  <v-chip
                    size="small"
                    closable
                    class="mr-1 group-chip"
                    color="secondary"
                    variant="flat"
                    :key="groupId"
                    style="max-width: 160px"
                    :title="getUserGroupById(groupId)?.name"
                    @click:close="removeTenantUserGroup(item.userId, groupId)"
                  >
                    <span class="text-truncate">
                      {{ getUserGroupById(groupId)?.name }}
                    </span>
                  </v-chip>
                </template>
              </template>
            </div>
          </template>
          <template v-slot:item._isOwner="{ item }">
            <v-checkbox readonly hide-details v-model="item._isOwner"></v-checkbox>
          </template>
          <template v-slot:item.controls="{ item }">
            <span v-if="item.id !== 'super-admin'">
              <v-menu>
                <template v-slot:activator="{ props }">
                  <v-btn icon variant="text" v-bind="props" size="small">
                    <v-icon>mdi-dots-horizontal</v-icon>
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item
                    link
                    @click="editUserRoles(item.userId)"
                    prepend-icon="mdi-pencil"
                  >
                    <v-list-item-title>Rollen bearbeiten</v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    link
                    @click="editUserGroups(item.userId)"
                    prepend-icon="mdi-account-group"
                  >
                    <v-list-item-title>Gruppen bearbeiten</v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    link
                    @click="removeTenantUser(item.userId)"
                    prepend-icon="mdi-close"
                  >
                    <v-list-item-title>Benutzer entfernen</v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    link
                    @click="addTenantOwner(item.userId)"
                    v-if="item._isOwner !== true"
                    prepend-icon="mdi-shield-crown"
                  >
                    <v-list-item-title>Zum Besitzer machen</v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    link
                    @click="removeTenantOwner(item.userId)"
                    v-if="item._isOwner === true"
                    prepend-icon="mdi-account"
                  >
                    <v-list-item-title>Zum Mitglied machen</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </span>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
    <TenantUserEditRoleDialog
      v-if="selectedUser"
      :user="selectedUser"
      :roles="api.roles"
      :open="showEditRolesDialog"
      @close="showEditRolesDialog = false"
      @save="editTenantUserRoles"
    />
    <TenantUserEditUserGroupDialog
      v-if="selectedUser"
      :user="selectedUser"
      :userGroups="api.userGroups"
      :open="showEditUserGroupsDialog"
      @close="showEditUserGroupsDialog = false"
      @save="editTenantUserGroups"
    />
  </AdminLayout>
</template>

<script>
import AdminLayout from "@/layouts/Admin.vue";
import { mapActions, mapGetters } from "vuex";
import ApiRolesService from "@/services/api/ApiRolesService";
import ApiTenantService from "@/services/api/ApiTenantService";
import ToastService from "@/services/ToastService";
import TenantUserEditRoleDialog from "@/components/Tenant/TenantUserEditRoleDialog.vue";
import TenantUserEditUserGroupDialog from "@/components/Tenant/TenantUserEditUserGroupDialog.vue";
import ApiUserGroupService from "../../services/api/ApiUserGroupService";

export default {
  components: {
    TenantUserEditRoleDialog,
    TenantUserEditUserGroupDialog,
    AdminLayout,
  },
  data() {
    return {
      valid: true,
      rules: {
        email: [
          (v) => !!v || "E-Mail ist erforderlich",
          (v) => /.+@.+\..+/.test(v) || "E-Mail muss gültig sein",
        ],
      },
      showEditRolesDialog: false,
      showEditUserGroupsDialog: false,
      selectedUser: null,
      isLoading: false,
      newUserId: null,
      newRoleIds: [],
      tenant: {
        users: [],
        ownerUserIds: [],
      },
      api: {
        users: [],
        roles: [],
        userGroups: [],
        owner: [],
      },
      headers: [
        {
          title: "Benutzer",
          align: "start",
          value: "userId",
        },
        {
          title: "Rollen",
          align: "start",
          value: "roles",
        },
        {
          title: "Benutzergruppe",
          align: "start",
          value: "groups",
          width: "400px",
        },
        {
          title: "Besitzer",
          align: "start",
          value: "_isOwner",
        },
        { title: "", value: "controls", sortable: false },
      ],
    };
  },
  computed: {
    ...mapGetters({
      loading: "loading/isLoading",
      tenantId: "tenants/currentTenantId",
      isInstanceOwner: "user/isInstanceOwner",
      isTenantOwner: "user/isTenantOwner",
    }),
    members() {
      const members = this.api.users.map((userRef) => ({
        ...userRef,
        _isOwner: this.api.owner.includes(userRef.userId),
      }));

      // There might be users marked as owners that do not have any roles assigned and therefore do not occur in tenant user references.
      const shadowMembers = this.api.owner
        .filter(
          (userId) =>
            !this.api.users.some((userRef) => userRef.userId === userId)
        )
        .map((userId) => ({
          userId: userId,
          roles: [],
          _isOwner: true,
        }));

      return [...members, ...shadowMembers];
    },
  },
  watch: {
    async tenantId() {
      await this.fetchRoles();
      await this.fetchUserGroups();
      await this.fetchTenantUsers();
    },
  },
  methods: {
    ...mapActions({
      startLoading: "loading/start",
      stopLoading: "loading/stop",
      addToast: "toasts/add",
    }),

    editUserRoles(userId) {
      this.selectedUser = this.api.users.find((user) => user.userId === userId);
      this.showEditRolesDialog = true;
    },

    editUserGroups(userId) {
      this.selectedUser = this.api.users.find((user) => user.userId === userId);
      this.showEditUserGroupsDialog = true;
    },

    async fetchRoles() {
      try {
        const response = await ApiRolesService.getTenantRoles(
          this.currentTenantId
        );
        this.api.roles = response.data;
      } catch (e) {
        console.error(e);
      }
    },

    async fetchUserGroups() {
      try {
        const response = await ApiUserGroupService.getUserGroups();
        this.api.userGroups = response;
      } catch (e) {
        console.error(e);
      }
    },

    async fetchTenantUsers() {
      await this.startLoading("fetch-users");
      try {
        const response = await ApiTenantService.getTenantUsers(this.tenantId);
        this.api.users = response.users;
        this.api.owner = response.owner;
      } catch (e) {
        console.error(e);
      } finally {
        await this.stopLoading("fetch-users");
      }
    },
    async removeTenantUser(userId) {
      try {
        this.isLoading = true;
        const response = await ApiTenantService.removeTenantUser(
          this.tenantId,
          userId
        );

        this.api.users = response.users;
        this.api.owner = response.owner;
      } catch (e) {
        console.error(e);
      } finally {
        this.isLoading = false;
      }
    },
    async addTenantUser() {
      const result = await this.$refs.form?.validate();
      if (!result.valid) {
        return;
      }
      try {
        this.isLoading = true;
        const response = await ApiTenantService.addTenantUser(
          this.tenantId,
          this.newUserId,
          this.newRoleIds
        );

        this.newUserId = null;
        this.newRoleIds = [];

        this.api.users = response.users;
        this.api.owner = response.owner;

        // reset validation after user has been added to the tenant
        this.$nextTick(() => {
          this.$refs.form?.resetValidation();
        });
      } catch (e) {
        if (e.response.status === 404) {
          await this.addToast(
            ToastService.createToast("tenant.error.user-not-found", "error")
          );
        } else {
          console.error(e);
        }
        console.error(e);
      } finally {
        this.isLoading = false;
      }
    },
    async removeTenantUserRole(userId, roleId) {
      try {
        if (!userId || !roleId) return;
        this.isLoading = true;
        const userRoles = this.members
          .find((member) => member.userId === userId)
          ?.roles?.filter((id) => id !== roleId);
        const response = await ApiTenantService.editTenantUserRoles(
          this.tenantId,
          userId,
          userRoles
        );
        this.api.users = response.users;
        this.api.owner = response.owner;
      } catch (e) {
        console.error(e);
      } finally {
        this.isLoading = false;
      }
    },
    async removeTenantUserGroup(userId, userGroupId) {
      try {
        if (!userId || !userGroupId) return;
        this.isLoading = true;
        const groups = this.members
          .find((member) => member.userId === userId)
          ?.groups?.filter((id) => id !== userGroupId);
        const response = await ApiTenantService.editTenantUserGroups(
          this.tenantId,
          userId,
          groups
        );
        this.api.users = response.users;
        this.api.owner = response.owner;
      } catch (e) {
        console.error(e);
      } finally {
        this.isLoading = false;
      }
    },
    async addTenantOwner(userId) {
      try {
        this.isLoading = true;
        const response = await ApiTenantService.addTenantOwner(
          this.tenantId,
          userId
        );
        this.api.owner = response.owner;
      } catch (e) {
        console.error(e);
      } finally {
        this.isLoading = false;
      }
    },
    async removeTenantOwner(userId) {
      try {
        this.isLoading = true;
        const response = await ApiTenantService.removeTenantOwner(
          this.tenantId,
          userId
        );
        this.api.owner = response.owner;
      } catch (e) {
        console.error(e);
      } finally {
        this.isLoading = false;
      }
    },
    async editTenantUserRoles(userId, roleIds) {
      try {
        this.isLoading = true;
        const response = await ApiTenantService.editTenantUserRoles(
          this.tenantId,
          userId,
          roleIds
        );

        this.api.users = response.users;
        this.api.owner = response.owner;
        this.showEditRolesDialog = false;
      } catch (e) {
        console.error(e);
      } finally {
        this.isLoading = false;
      }
    },
    async editTenantUserGroups(userId, groupIds) {
      try {
        this.isLoading = true;
        const response = await ApiTenantService.editTenantUserGroups(
          this.tenantId,
          userId,
          groupIds
        );

        this.api.users = response.users;
        this.api.owner = response.owner;
        this.showEditUserGroupsDialog = false;
      } catch (e) {
        console.error(e);
      } finally {
        this.isLoading = false;
      }
    },
    getRoleById(roleId) {
      const role = this.api.roles.find((role) => role.id === roleId);
      return role;
    },
    getUserGroupById(groupId) {
      const userGroup = this.api.userGroups.find(
        (group) => group.id === groupId
      );
      return userGroup;
    },
  },
  async created() {
    await this.fetchRoles();
    await this.fetchUserGroups();
    await this.fetchTenantUsers();
  },
};
</script>

<style scoped>
.chip-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 6px;
}
::v-deep(.group-chip .v-chip__content) {
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
